import React, { useEffect, useMemo } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import Header from "../../common/Header";
import SideBar from "../../common/SideBar";
import Button from "../../common/Button/Button";
import SkeletonTicketList from "../../../loaders/SkeletonTicketList";
import { useOhsAccess } from "../../../query/hooks/QueryHooksFirstAidBox";
import { OHS_BASE_PATH, PEOPLE_ROLES } from "./ohsConstants";
import BoxList from "./BoxList";
import BoxDetails from "./BoxDetails";
import BoxCheckForm from "./BoxCheckForm";
import ManagerOverview from "./ManagerOverview";
import RestockList from "./RestockList";
import Discrepancies from "./Discrepancies";
import UsageReport from "./UsageReport";
import CheckHistory from "./CheckHistory";
import BuildingCheckList from "./BuildingCheckList";
import BuildingCheckForm from "./BuildingCheckForm";
import BuildingQuestionEditor from "./BuildingQuestionEditor";
import BuildingCheckHistory from "./BuildingCheckHistory";
import Analytics from "./Analytics";
import ItemCatalog from "./ItemCatalog";
import People from "./People";
import AuditTrail from "./AuditTrail";
import { DesktopOnlyNav, OhsTabContent } from "./Ohs.styles";

/**
 * Tabs in display order, each with the access flag (useOhsAccess) it needs.
 */
const tabs = [
    { key: "boxes", label: "Boxes", icon: "fa-first-aid", accessFlag: "canViewBoxes" },
    { key: "buildingChecks", label: "Building Checks", icon: "fa-building", accessFlag: "canManage" },
    { key: "overview", label: "Overview", icon: "fa-chart-pie", accessFlag: "canManage" },
    { key: "restock", label: "Restock List", icon: "fa-boxes", accessFlag: "canManage" },
    { key: "discrepancies", label: "Discrepancies", icon: "fa-not-equal", accessFlag: "canManage" },
    { key: "usage", label: "Usage", icon: "fa-hand-holding-medical", accessFlag: "canManage" },
    { key: "checkHistory", label: "Check History", icon: "fa-clipboard-list", accessFlag: "canManage" },
    { key: "buildingCheckHistory", label: "Building Check History", icon: "fa-file-check", accessFlag: "canManage" },
    { key: "analytics", label: "Analytics", icon: "fa-chart-bar", accessFlag: "canManage" },
    { key: "catalog", label: "Item Catalog", icon: "fa-list-ul", accessFlag: "canManage" },
    { key: "firstAiders", label: "First Aiders", icon: "fa-user-nurse", accessFlag: "canManagePeople" },
    { key: "ohsRepresentatives", label: "OHS Representatives", icon: "fa-user-shield", accessFlag: "canManagePeople" },
    { key: "auditTrail", label: "Audit Trail", icon: "fa-history", accessFlag: "canManage" },
];

/**
 * Occupational Health and Safety - First Aid Box Management page (tabs + tab content).
 * The OHS Management permission sees every tab; otherwise tabs follow the user's place on the
 * First Aiders / Managers lists.
 * Route: /campusAdministration/ohs/:tab/:type?/:id?
 */
function Ohs() {
    const history = useHistory();
    const { tab, type, id } = useParams();
    const { data: ohsAccess, isLoading: isAccessLoading, isPermissionLoading } = useOhsAccess();
    const allowedTabs = useMemo(() => tabs.filter((ohsTab) => ohsAccess[ohsTab.accessFlag]), [ohsAccess]);
    const activeTab = allowedTabs.find((ohsTab) => ohsTab.key === tab);
    // A check form ends in its buttons, which the mobile bottom nav would cover.
    const isCheckPage = ["boxes", "buildingChecks"].includes(tab) && type === "check";

    useEffect(() => {
        if (isAccessLoading) {
            return;
        }
        if (!ohsAccess.canView && !isPermissionLoading) {
            history.push("/noaccess");
            return;
        }
        if (!activeTab && allowedTabs.length) {
            history.replace(`${OHS_BASE_PATH}/${allowedTabs[0].key}`);
        }
    }, [isAccessLoading, ohsAccess.canView, isPermissionLoading, activeTab, allowedTabs, history]);

    const renderTabContent = () => {
        if (isAccessLoading || !activeTab) {
            return <SkeletonTicketList />;
        }
        if (tab === "boxes" && type === "open") {
            return <BoxDetails boxId={id} />;
        }
        if (tab === "boxes" && type === "check") {
            return <BoxCheckForm boxId={id} />;
        }
        if (tab === "buildingChecks" && type === "check") {
            return <BuildingCheckForm buildingId={id} />;
        }
        if (tab === "buildingChecks" && type === "checklist") {
            return <BuildingQuestionEditor />;
        }

        switch (tab) {
            case "overview":
                return <ManagerOverview />;
            case "restock":
                return <RestockList />;
            case "discrepancies":
                return <Discrepancies />;
            case "usage":
                return <UsageReport />;
            case "checkHistory":
                return <CheckHistory />;
            case "buildingChecks":
                return <BuildingCheckList />;
            case "buildingCheckHistory":
                return <BuildingCheckHistory />;
            case "analytics":
                return <Analytics />;
            case "catalog":
                return <ItemCatalog />;
            case "firstAiders":
                return <People role={PEOPLE_ROLES.firstAider} />;
            case "ohsRepresentatives":
                return <People role={PEOPLE_ROLES.ohsRepresentative} />;
            case "auditTrail":
                return <AuditTrail />;
            default:
                return <BoxList />;
        }
    };

    return (
        <>
            <Header />
            <div className="content-wrapper d-flex flex-wrap new-standard-box">
                <DesktopOnlyNav $isDesktopOnly={isCheckPage}>
                    <SideBar />
                </DesktopOnlyNav>
                <div className="sidebar-right">
                    <div className="sub-menu-content-block">
                        <div className="sub-menu-right-block">
                            <div className="wrap-box-right">
                                <div className="my-tickets-heading-blk flex-direction mb-2">
                                    <div className="d-flex align-items-center">
                                        <div className="name-w-head d-flex align-items-center">
                                            <h4 className="text-left-align landing-heading heading_color_dynamic">
                                                Occupational Health <span>and Safety</span>
                                            </h4>
                                        </div>
                                        <div className="text-md-right action2-blk">
                                            <div className="ticket-view-btn d-flex align-items-center">
                                                <div className="add-new-ticket-bx">
                                                    <Button
                                                        variant="secondary"
                                                        iconLeft={<i className="fal fa-chevron-left" />}
                                                        onClick={() => history.goBack()}
                                                        title="Back"
                                                        className="btn-white-bordered"
                                                    >
                                                        Back
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="tabs-wrap">
                                    <DesktopOnlyNav $isDesktopOnly={isCheckPage}>
                                        <ul className="nav nav-pills mb-4" id="tabs" role="tablist">
                                            {allowedTabs.map((ohsTab) => (
                                                <li className="nav-item" key={ohsTab.key}>
                                                    <Link
                                                        className={`nav-link ${tab === ohsTab.key ? "active" : ""}`}
                                                        to={`${OHS_BASE_PATH}/${ohsTab.key}`}
                                                        role="tab"
                                                        aria-selected={tab === ohsTab.key}
                                                    >
                                                        <i className={`fal ${ohsTab.icon}`}></i>
                                                        {ohsTab.label}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </DesktopOnlyNav>
                                    <div className="tab-content" id="pills-tabContent">
                                        <div className="tab-pane fade active show" role="tabpanel">
                                            <OhsTabContent>
                                                {renderTabContent()}
                                            </OhsTabContent>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default Ohs;
